import type { GetMeUserResponse } from "@nlc/shared-types";

import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import Cookies from "js-cookie";
import wretch from "wretch";

import { env } from "@/config/env";

const BASE_URL = env.VITE_BASE_API_URL;
const APP_ID = env.VITE_INTERCOM_APP_ID;

const CUSTOMER_ROLES = new Set(["owner", "admin", "collaborator"]);
const SUPER_ADMIN_ROLE = "super-admin";

/** Known NLC domains where Intercom should always be active */
const NLC_DOMAINS = new Set([
  "localhost",
  "client.nlc.com",
  "mb-staging.nlc.com",
  "mb-dev.nlc.com",
]);

const isCustomerRole = (roleName?: string): boolean => {
  if (!roleName) return false;

  return CUSTOMER_ROLES.has(roleName);
};

const shouldBlockIntercomDueToImpersonation = async (): Promise<boolean> => {
  try {
    const jwtCookie = Cookies.get("JWT");

    if (!jwtCookie) return false;

    const parts = jwtCookie.split(".");

    if (parts.length !== 3) return false;

    const payloadPart = parts[1];

    if (!payloadPart) return false;

    const payload = JSON.parse(atob(payloadPart));

    // If there's no impersonator, allow Intercom
    if (!payload?.impersonatorId) return false;

    // At this point we know the user is being impersonated
    // Check if the impersonator is a super admin
    try {
      const impersonatorResponse = await wretch(`${BASE_URL}/api/v1`)
        .options({
          credentials: "include",
        })
        .url(`/users/${payload.impersonatorId}`)
        .get()
        .json<{ user: { roleName?: string } }>();

      // If impersonator is a super admin, allow Intercom
      return impersonatorResponse.user.roleName !== SUPER_ADMIN_ROLE;
    } catch (apiError) {
      console.error("Failed to check impersonator role:", apiError);

      return true;
    }
  } catch (error) {
    console.error("Error checking impersonation status:", error);

    return false;
  }
};

let isBooted = false;

/**
 * Ends the Intercom session so the next login boots with the new user's
 * identity. Must be called on logout: navigation is client-side, so this
 * module's state survives until a full page reload.
 */
export function shutdownIntercom(): void {
  if (!isBooted) return;

  try {
    shutdown();
  } catch (error) {
    console.error("Failed to shut down Intercom", error);
  } finally {
    isBooted = false;
  }
}

export async function initIntercom(): Promise<void> {
  try {
    if (isBooted) return;
    isBooted = true;

    // Skip Intercom on whitelabel domains
    const domain = window.location.hostname;

    if (!NLC_DOMAINS.has(domain)) {
      console.warn("Non-NLC domain detected, skipping Intercom initialization");
      isBooted = false;

      return;
    }

    const userData = await wretch(`${BASE_URL}/api/v1`)
      .options({
        credentials: "include",
      })
      .url("/users/me")
      .get()
      .unauthorized(() => null)
      .json<GetMeUserResponse>();

    if (!userData) {
      console.warn("No user data found, skipping Intercom initialization");
      isBooted = false;

      return;
    }

    // Only initialize for customer roles
    if (!userData.user.roleName || !isCustomerRole(userData.user.roleName)) {
      console.warn("Not a customer role, skipping Intercom initialization");
      isBooted = false;

      return;
    }

    // Check if we should block Intercom due to impersonation
    if (await shouldBlockIntercomDueToImpersonation()) {
      console.warn("Should block Intercom due to impersonation");
      isBooted = false;

      return;
    }

    // shutdownIntercom() may have run while we were awaiting
    if (!isBooted) return;

    Intercom({
      app_id: APP_ID,
      email: userData.user.email,
      name: userData.user.name,
      user_id: userData.user.id.toString(),
    });

    console.log("Intercom initialized");
  } catch (error) {
    isBooted = false;
    console.error("Failed to initialize Intercom", error);
  }
}
